import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
  ArcElement,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { startOfISOWeek, addDays, getISOWeek } from "date-fns";
import "chartjs-adapter-date-fns";
import { sv } from "date-fns/locale";
import zoomPlugin from "chartjs-plugin-zoom";
import "./Chart.scss";
import { buildDateMap } from "./ChartUtils";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
  ArcElement,
  zoomPlugin
);

interface GuestChartProps {
  rows: any[];
  startDate: string;
  endDate: string;
}


export function GuestChart({ rows = [], startDate, endDate }: GuestChartProps) {
  const [groupByWeek, setGroupByWeek] = useState(false);
  const [chartMax, setChartMax] = useState<number | null>(null);

  // --- Identify summary rows ---
  const summaryRows = rows.filter((r) => r.type === "summary");

  // --- Weekdays dynamically ---
  const weekdays = Array.from(
    new Set(
      summaryRows.flatMap((row) =>
        Object.keys(row).filter(
          (k) =>
            !["__typename", "memberType", "week", "total", "type"].includes(
              k
            ) &&
            typeof row[k] === "object" &&
            Object.values(row[k]).some((v) => v !== null)
        )
      )
    )
  );

  const weeks = Array.from(
    new Set(rows.filter((r) => r.week).map((r) => r.week))
  );

  // --- Build date-week-day mapping (ISO-year safe) ---
  const dateMap = buildDateMap(startDate, endDate);

  const points: { date: Date; week: number; weekday: string }[] = [];

  summaryRows.forEach((row) => {
    const week = row.week;

    weekdays.forEach((day) => {
      const cell = row?.[day];
      if (!cell || !Object.values(cell).some((v) => v !== null)) return;

      const date = dateMap.get(`${week}-${day}`);
      if (!date) return;

      points.push({ date, week, weekday: day });
    });
  });

  points.sort((a, b) => a.date.getTime() - b.date.getTime());

  // --- Group by memberType ---
  const grouped: Record<string, any[]> = rows
    .filter((r) => r.type === "row" && r.memberType !== "Totalt")
    .reduce((acc, row) => {
      const name = row.memberType?.trim() ?? "Okänd";
      if (!acc[name]) acc[name] = [];
      acc[name].push(row);
      return acc;
    }, {});

  // --- Color palette ---
  const colors = [
    "#3F9C30",
    "#309C49",
    "#309C6B",
    "#309C8C",
    "#30849C",
    "#30629C",
    "#303F9C",
    "#49309C",
    "#6B309C",
    "#8C309C",
    "#2D6E22",
    "#24753B",
    "#247559",
    "#24757A",
    "#246C84",
    "#214E84",
    "#232E84",
    "#3A237D",
    "#52237D",
    "#70237D",
  ];

  // --- Grouped points (daily or weekly) ---
  const displayPoints = groupByWeek
    ? Array.from(new Map(points.map((p) => [p.week, p])).values())
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map((p) => ({
          date: p.date,
          week: p.week,
          weekday: "monday",
        }))
    : points;

  // --- Build datasets (onlyGuest lines) ---
  const memberDatasets = Object.entries(grouped).map(
    ([memberType, memberRows], idx) => {
      const data = groupByWeek
        ? displayPoints.map(({ week }) => {
            const weekRows = memberRows.filter((r) => r.week === week);
            return weekRows.reduce((sum, r) => {
              return (
                sum +
                weekdays.reduce((inner, day) => {
                  const val = r?.[day]?.onlyGuest ?? 0;
                  return inner + (typeof val === "number" ? val : 0);
                }, 0)
              );
            }, 0);
          })
        : displayPoints.map(({ week, weekday }) => {
            const weekRow = memberRows.find((r) => r.week === week);
            const cell = weekRow?.[weekday];
            return cell?.onlyGuest ?? 0;
          });

      // Store totals too (for tooltip context)
      const totals = displayPoints.map(({ week, weekday }) => {
        const weekRow = memberRows.find((r) => r.week === week);
        const cell = weekRow?.[weekday];
        return cell?.total ?? 0;
      });

      return {
        label: memberType,
        data,
        customTotals: totals,
        borderWidth: 2,
        tension: 0.1,
        borderColor: colors[idx % colors.length],
        backgroundColor: colors[idx % colors.length] + "55",
        spanGaps: false,
      };
    }
  );

  // --- Add aggregated "Alla medlemskategorier" line ---
  const allMemberData = displayPoints.map(({ week, weekday }) => {
    return Object.values(grouped).reduce((sum, memberRows: any[]) => {
      const weekRow = memberRows.find((r) => r.week === week);
      const c = weekRow?.[weekday]?.onlyGuest ?? 0;
      return sum + (typeof c === "number" ? c : 0);
    }, 0);
  });

  const allMembersDataset = {
    label: "Alla valda spelrätter",
    data: allMemberData,
    borderWidth: 2,
    tension: 0.1,
    borderColor: "#30389c",
    backgroundColor: "#30389c55",
    borderDash: [4, 2],
    spanGaps: false,
  };

  // --- Capacity line (from summary total bookings) ---
  const capacityData = groupByWeek
    ? Array.from(new Set(points.map((p) => p.week))).map((week) => {
        const weekRow = summaryRows.find((r) => r.week === week);
        return weekdays.reduce((sum, day) => {
          const val = weekRow?.[day]?.total ?? 0;
          return sum + (typeof val === "number" ? val : 0);
        }, 0);
      })
    : points.map(({ week, weekday }) => {
        const weekRow = summaryRows.find((r) => r.week === week);
        const val = weekRow?.[weekday]?.total ?? 0;
        return typeof val === "number" ? val : 0;
      });

  const capacityDataset = {
    label: "Totala bokningar (kapacitet)",
    data: capacityData,
    borderWidth: 1.5,
    borderColor: "#999",
    borderDash: [6, 4],
    pointRadius: 3,
    pointBackgroundColor: "transparent",
    tension: 0.1,
    spanGaps: false,
  };

  // --- Combine all datasets ---
  const datasets = [capacityDataset, allMembersDataset, ...memberDatasets];
  const xLabels = groupByWeek
    ? displayPoints.map((p) => `Vecka ${p.week}`)
    : displayPoints.map((p) => p.date);

  const allValues = datasets.flatMap((d) => d.data as number[]);
  const maxY = Math.max(...allValues, 10);

  const numericValues = datasets
    .flatMap((d) => d.data)
    .filter((v): v is number => v != null && !isNaN(v));

  const maxValue = Math.max(...numericValues, 10);

  useEffect(() => {
    if (maxValue) setChartMax(Math.ceil(maxValue * 1.1));
  }, [maxValue, groupByWeek]);

  const rangeOptions = [0.25, 0.5, 0.75, 1.1, 1.5].map((factor) => ({
    label: `${Math.round(maxValue * factor)}`,
    value: Math.round(maxValue * factor),
  }));

  if (!rows || rows.length === 0) return <div>Diagram</div>;
  if (!summaryRows.length) return <div>Ingen summerad rad hittades</div>;

  // --- Render chart ---
  return (
    <div>
      <div className="chart-controls">
        <div className="chart-view-toggle">
          Visa per vecka
          <label className="toggle text-m">
            <input
              type="checkbox"
              checked={groupByWeek}
              onChange={(e) => setGroupByWeek(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>
        <div className="chart-max-select">
          <label>Maxvärde:</label>
          <select
            className="chart-max-select-dropdown"
            value={chartMax ?? ""}
            onChange={(e) =>
              setChartMax(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value={Math.ceil(maxValue * 1.1)}>Auto</option>
            {rangeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Line
        data={{
          labels: xLabels as any,
          datasets,
        }}
        options={{
          scales: {
            x: {
              type: groupByWeek ? "category" : "time",
              time: groupByWeek
                ? undefined
                : {
                    unit: "day",
                    tooltipFormat: "EEE dd/MM",
                    displayFormats: { day: "EEE dd/MM" },
                  },
              adapters: { date: { locale: sv } },
              title: { display: true, text: groupByWeek ? "Vecka" : "Datum" },
            },
            y: {
              beginAtZero: true,
              suggestedMax: maxValue,
              min: -5,
              max: Number(chartMax),
              title: { display: true, text: "Endast gästbokningar" },
            },
          },
          plugins: {
            legend: { position: "top" as const },
            tooltip: {
              callbacks: {
                label: function (context: any) {
                  const dataset = context.dataset as any;
                  const label = dataset.label || "";
                  const onlyGuest = context.parsed.y;
                  const total =
                    dataset.customTotals?.[context.dataIndex] ?? undefined;

                  // Capacity line tooltip
                  if (label === "Totala bokningar (kapacitet)") {
                    return [`${label}`, `Kapacitet: ${onlyGuest}`];
                  }

                  // Regular membertype line tooltip
                  const lines = [label];
                  lines.push(`Endast gäst: ${onlyGuest}`);
                  if (total !== undefined && total !== 0) {
                    lines.push(`Bokningar: ${total}`);
                  }
                  return lines;
                },
              },
            },
          },
        }}
      />
    </div>
  );
}
